import React, { useState, useEffect } from 'react';
import UnsavedChangesGuard from '../components/UnsavedChangesGuard';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useSafeNavigate, useUnsavedChangesContext } from '../hooks/useUnsavedChangesWarning';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';

import { tagsAPI } from '../services/api';
import { TagChip } from '../utils/tagUtils';

// Couleurs prédéfinies (18 couleurs : 9 par ligne, 2 lignes)
const PREDEFINED_COLORS = [
  '#EF4444', // Rouge
  '#F97316', // Orange
  '#EAB308', // Jaune
  '#84CC16', // Vert clair
  '#22C55E', // Vert
  '#14B8A6', // Turquoise
  '#38BDF8', // Bleu clair
  '#3B82F6', // Bleu
  '#8B5CF6', // Violet
  '#A78BFA', // Lavande
  '#EC4899', // Rose
  '#F43F5E', // Rose-rouge
  '#6B7280', // Gris foncé
  '#DC2626', // Rouge foncé
  '#EA580C', // Orange foncé
  '#CA8A04', // Jaune foncé
  '#65A30D', // Vert foncé
  '#059669', // Vert émeraude
];

// Fonction pour convertir hex en rgba
const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(25, 118, 210, ${alpha})`;
  const num = parseInt(hex.replace('#', ''), 16);
  const R = (num >> 16) & 255;
  const G = (num >> 8) & 255;
  const B = num & 255;
  return `rgba(${R}, ${G}, ${B}, ${alpha})`;
};

function ModifierTagPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useSafeNavigate();
  const queryClient = useQueryClient();
  const { allowNextNavigation, setHasUnsavedChanges: setGlobalHasUnsavedChanges } = useUnsavedChangesContext();
  const isEditMode = !!id;

  // État pour détecter les modifications non sauvegardées
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { control, handleSubmit, formState: { errors, isDirty }, reset, watch, setValue } = useForm({
    defaultValues: {
      nom: '',
      description: '',
      couleur: '#EF4444' // Rouge par défaut
    }
  });

  // Surveiller les valeurs pour l'aperçu
  const nomValue = useWatch({ control, name: 'nom' });
  const couleurValue = useWatch({ control, name: 'couleur' });

  // Récupérer les données du tag si en mode édition
  const { data: tag, isLoading, error } = useQuery(
    ['tag', id],
    () => tagsAPI.getById(id).then(res => res.data.data),
    {
      enabled: isEditMode
    }
  );

  // Surveiller les modifications du formulaire
  useEffect(() => {
    setHasUnsavedChanges(isDirty);
    setGlobalHasUnsavedChanges(isDirty, t('modifierTagPage.unsavedChangesMessage'));
  }, [isDirty, setGlobalHasUnsavedChanges, t]);

  // Pré-remplir le formulaire quand les données sont disponibles
  useEffect(() => {
    if (tag) {
      reset({
        nom: tag.nom || '',
        description: tag.description || '',
        couleur: tag.couleur || '#EF4444'
      });
    } else if (!isEditMode) {
      reset({
        nom: '',
        description: '',
        couleur: '#EF4444'
      });
    }
  }, [tag, reset, isEditMode]);

  // Mutation pour créer un tag
  const createTagMutation = useMutation(
    (data) => tagsAPI.create(data),
    {
      onSuccess: (response) => {
        allowNextNavigation();
        setGlobalHasUnsavedChanges(false);
        toast.success(t('modifierTagPage.createSuccess'));
        queryClient.invalidateQueries('tags');
        queryClient.invalidateQueries('tags-list');
        queryClient.invalidateQueries('livres');
        const newTagId = response.data?.data?.id;
        if (newTagId) {
          navigate(`/tags/${newTagId}`);
        } else {
          navigate('/tags');
        }
      },
      onError: (error) => {
        console.error('Erreur lors de la création:', error);
        
        if (error.response?.status === 409) {
          toast.error(t('modifierTagPage.alreadyExists'));
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          errors.forEach(err => {
            if (err.message) {
              toast.error(err.message);
            }
          });
        } else {
          toast.error(t('modifierTagPage.createError'));
        }
      }
    }
  );

  // Mutation pour mettre à jour le tag
  const updateTagMutation = useMutation(
    (data) => tagsAPI.update(id, data),
    {
      onSuccess: () => {
        allowNextNavigation();
        setGlobalHasUnsavedChanges(false);
        toast.success(t('modifierTagPage.updateSuccess'));
        queryClient.invalidateQueries(['tag', id]);
        queryClient.invalidateQueries('tags');
        queryClient.invalidateQueries('tags-list');
        queryClient.invalidateQueries('livres');
        navigate(`/tags/${id}`);
      },
      onError: (error) => {
        console.error('Erreur lors de la modification:', error);
        
        if (error.response?.status === 409) {
          toast.error(t('modifierTagPage.alreadyExists'));
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          errors.forEach(err => {
            if (err.message) {
              toast.error(err.message);
            }
          });
        } else {
          toast.error(t('modifierTagPage.updateError'));
        }
      }
    }
  );

  const onSubmit = (data) => {
    if (isEditMode) {
      updateTagMutation.mutate(data);
    } else {
      createTagMutation.mutate(data);
    }
  };

  const handleBack = () => {
    if (isEditMode && id) {
      navigate(`/tags/${id}`);
    } else {
      navigate('/tags');
    }
  };

  const isLoadingMutation = createTagMutation.isLoading || updateTagMutation.isLoading;

  if (isEditMode && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isEditMode && error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('modifierTagPage.loadingError', { message: error.message })}
        </Alert>
      </Box>
    );
  }

  if (isEditMode && !tag) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {t('modifierTagPage.notFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header avec navigation */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <BackIcon />
            </IconButton>
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{ 
                flexGrow: 1, 
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 500,
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              }}
            >
              {isEditMode ? t('modifierTagPage.editTitle') : t('modifierTagPage.addTitle')}
            </Typography>
            <Button
              type="submit"
              variant="contained"
              startIcon={isLoadingMutation ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={isLoadingMutation}
              sx={{ 
                ml: 2,
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {isLoadingMutation ? t('modifierTagPage.saving') : t('modifierTagPage.save')}
            </Button>
          </Box>
        </Box>

        {/* Formulaire */}
        
        {/* Section Libellé */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body1" 
            component="label"
            sx={{ 
              display: 'block',
              mb: 1,
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('modifierTagPage.label')}
          </Typography>
          <Controller
            name="nom"
            control={control}
            rules={{ 
              required: t('modifierTagPage.nameRequired'),
              maxLength: { value: 50, message: t('modifierTagPage.nameMaxLength') }
            }}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  fullWidth
                  placeholder={t('modifierTagPage.namePlaceholder')}
                  error={!!errors.nom}
                  helperText={errors.nom?.message || `${field.value?.length || 0}${t('modifierTagPage.charactersCount')}`}
                  disabled={isLoadingMutation}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.default',
                      borderRadius: 2,
                    }
                  }}
                />
              </>
            )}
          />
        </Box>

        {/* Section Couleur */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body1" 
            component="label"
            sx={{ 
              display: 'block',
              mb: 2,
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('modifierTagPage.color')}
          </Typography>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(9, 1fr)', 
            gap: 0.5,
            mb: 3
          }}>
            {PREDEFINED_COLORS.map((color) => (
              <Box
                key={color}
                onClick={() => setValue('couleur', color, { shouldDirty: true })}
                sx={{
                  width: '36px',
                  aspectRatio: '1',
                  backgroundColor: color,
                  borderRadius: 0.75,
                  cursor: 'pointer',
                  marginBottom: '10px',
                  border: couleurValue === color ? '2px solid #000' : '1px solid rgba(0,0,0,0.1)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }
                }}
              />
            ))}
          </Box>

          {/* Couleur personnalisée */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Controller
              name="couleur"
              control={control}
              render={({ field }) => (
                <>
                  <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Box
                      component="input"
                      type="color"
                      value={field.value || '#EF4444'}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setValue('couleur', e.target.value, { shouldDirty: true });
                      }}
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                        zIndex: 1,
                      }}
                    />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        backgroundColor: field.value || '#EF4444',
                        border: '2px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }
                      }}
                    />
                  </Box>
                  <Typography 
                    variant="body2"
                    sx={{ 
                      fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                    }}
                  >
                    {t('modifierTagPage.or')}
                  </Typography>
                  <TextField
                    {...field}
                    type="text"
                    placeholder={t('modifierTagPage.colorPlaceholder')}
                    disabled={isLoadingMutation}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Valider que c'est un code hex valide
                      if (value === '' || /^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        field.onChange(value);
                        setValue('couleur', value, { shouldDirty: true });
                      }
                    }}
                    sx={{
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.default',
                        borderRadius: 2,
                      }
                    }}
                  />
                </>
              )}
            />
          </Box>
        </Box>

        {/* Section Description */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body1" 
            component="label"
            sx={{ 
              display: 'block',
              mb: 1,
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('modifierTagPage.description')}
          </Typography>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                placeholder={t('modifierTagPage.descriptionPlaceholder')}
                multiline
                rows={4}
                disabled={isLoadingMutation}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.default',
                    borderRadius: 2,
                  }
                }}
              />
            )}
          />
        </Box>

        {/* Section Aperçu */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body1" 
            component="label"
            sx={{ 
              display: 'block',
              mb: 2,
              fontWeight: 500,
              fontSize: '14px',
              fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
            }}
          >
            {t('modifierTagPage.preview')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TagChip
              tag={{
                nom: nomValue || t('modifierTagPage.previewDefault'),
                couleur: couleurValue || '#EF4444',
              }}
              size="small"
            />
          </Box>
        </Box>
      </form>

      {/* Guard pour bloquer la navigation en cas de modifications non sauvegardées */}
      <UnsavedChangesGuard
        hasUnsavedChanges={hasUnsavedChanges}
        message={t('modifierTagPage.unsavedChangesMessage')}
      />
    </Box>
  );
}

export default ModifierTagPage;

